import type { ReactNode } from 'react'
import { AlertTriangle, RefreshCw, Search, Tags } from 'lucide-react'
import { useSearchParams } from 'react-router'
import { ApiContractError, ApiError } from '@/api/client'
import type { TemuProducttypeCategory } from '@/api/temuProducttypes/types'
import { filterTemuProducttypes } from '@/features/temuProducttypes/temuProducttypeRows'
import { useTemuProducttypes } from '@/features/temuProducttypes/useTemuProducttypes'

function displayProducttypeName(row: TemuProducttypeCategory): string {
  return row.producttypeName
}

function LoadingRows(): ReactNode {
  return Array.from({ length: 5 }, (_, index) => (
    <tr key={index} className="border-t border-slate-100 dark:border-slate-800">
      <td className="px-5 py-4" colSpan={4}>
        <div className="h-5 animate-pulse rounded bg-slate-100 dark:bg-slate-800" />
      </td>
    </tr>
  ))
}

export function TemuProducttypesPage(): ReactNode {
  const [searchParams, setSearchParams] = useSearchParams()
  const search = searchParams.get('q') ?? ''
  const { data = [], isLoading, isFetching, error, refetch } = useTemuProducttypes()
  const filteredRows = filterTemuProducttypes(data, search)
  const producttypeCount = new Set(data.map((row) => row.producttypeKey)).size
  const categoryCount = new Set(data.map((row) => row.categoryId)).size

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
      <section className="overflow-hidden rounded-3xl bg-slate-950 px-6 py-8 text-white shadow-xl sm:px-10 sm:py-10">
        <div className="flex flex-col gap-7 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-teal-300/25 bg-teal-300/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-teal-200">
              <Tags aria-hidden="true" size={15} strokeWidth={2} />
              Temu Kategorien
            </div>
            <h1 className="mt-5 text-3xl font-black tracking-tight sm:text-4xl">
              Category-IDs der Temu-Produkttypen
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-300 sm:text-base">
              Diese Übersicht zeigt die für Temu hinterlegten Produkttypen und ihre
              Kategoriezuordnung.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            {[
              { label: 'Zuordnungen', value: data.length },
              { label: 'Produkttypen', value: producttypeCount },
              { label: 'Kategorien', value: categoryCount },
            ].map((metric) => (
              <div
                key={metric.label}
                className="min-w-20 rounded-2xl border border-white/10 bg-white/10 px-3 py-3 sm:min-w-28 sm:px-4"
              >
                <p className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-slate-400">
                  {metric.label}
                </p>
                <p className="mt-1 text-2xl font-bold tabular-nums">{metric.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <label className="relative block w-full sm:max-w-md">
          <span className="sr-only">Produkttypen und Kategorien durchsuchen</span>
          <Search
            aria-hidden="true"
            size={18}
            className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            type="search"
            value={search}
            onChange={(event) => {
              const nextSearch = event.target.value
              setSearchParams(nextSearch === '' ? {} : { q: nextSearch }, { replace: true })
            }}
            placeholder="Produkttyp, Schlüssel, Kategorie oder ID"
            className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-10 pr-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
          />
        </label>

        <button
          type="button"
          onClick={() => {
            void refetch()
          }}
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
        >
          <RefreshCw
            aria-hidden="true"
            size={16}
            className={isFetching ? 'animate-spin' : undefined}
          />
          Aktualisieren
        </button>
      </div>

      {error !== null && (
        <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 p-5 text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-100">
          <div className="flex items-start gap-2">
            <AlertTriangle aria-hidden="true" size={19} className="mt-0.5 shrink-0" />
            <p className="text-sm font-medium">
              {error instanceof ApiError || error instanceof ApiContractError
                ? error.message
                : 'Die Temu-Produkttypen konnten nicht geladen werden.'}
            </p>
          </div>
        </div>
      )}

      <section className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <div className="flex items-center justify-between gap-4 border-b border-slate-200 px-5 py-4 dark:border-slate-700">
          <div>
            <h2 className="font-semibold text-slate-900 dark:text-slate-100">Produkttypen</h2>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              {search.trim() === ''
                ? `${String(data.length)} Temu-Zuordnungen`
                : `${String(filteredRows.length)} von ${String(data.length)} Treffern`}
            </p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-800/60 dark:text-slate-400">
              <tr>
                <th scope="col" className="px-5 py-3 font-semibold">
                  Produkttyp
                </th>
                <th scope="col" className="px-5 py-3 font-semibold">
                  Schlüssel
                </th>
                <th scope="col" className="px-5 py-3 font-semibold">
                  Category-ID
                </th>
                <th scope="col" className="px-5 py-3 font-semibold">
                  Temu-Kategorie
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <LoadingRows />
              ) : (
                filteredRows.map((row, index) => (
                  <tr
                    key={`${row.producttypeKey}-${String(index)}`}
                    className="border-t border-slate-100 text-slate-700 dark:border-slate-800 dark:text-slate-200"
                  >
                    <td className="px-5 py-4 font-semibold text-slate-900 dark:text-slate-100">
                      {displayProducttypeName(row)}
                    </td>
                    <td className="px-5 py-4 font-mono text-xs text-slate-500 dark:text-slate-400">
                      {row.producttypeKey}
                    </td>
                    <td className="px-5 py-4">
                      <span className="font-mono font-semibold tabular-nums text-slate-950 dark:text-white">
                        {String(row.categoryId)}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-slate-600 dark:text-slate-300">
                      {row.categoryName ?? 'Keine Kategorie hinterlegt'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!isLoading && filteredRows.length === 0 ? (
          <div className="border-t border-slate-100 px-6 py-14 text-center dark:border-slate-800">
            <p className="font-semibold text-slate-800 dark:text-slate-100">
              {data.length === 0
                ? 'Keine Temu-Zuordnungen gefunden.'
                : 'Keine Produkttypen passen zur Suche.'}
            </p>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              {data.length === 0
                ? 'Sobald eine Temu-Kategoriezuordnung hinterlegt ist, erscheint sie hier.'
                : 'Suche nach Name, Schlüssel, Kategorie oder Category-ID.'}
            </p>
          </div>
        ) : null}
      </section>
    </div>
  )
}
